import { ProfileChipContainer } from 'components/Chips/ChipContainers';
import MentorStatusChip from 'components/Chips/MentorStatusChip';
import RoleChip from 'components/Chips/RoleChip';
import { DefaultLoader, LoaderContainer } from 'components/Loader';
import {
  HeadButtons,
  PageBack,
  PageHeadContainer,
  PageTitle,
} from 'components/PageHead';
import ProfileNavigation from 'components/profileNavigation';
import ProfileLink from 'components/profileNavigation/Link';
import { MentorStatusValues } from 'enums/MentorStatus';
import { RoleValues } from 'enums/Roles';
import React, { FC, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import useSWR from 'swr';
import User from 'types/User';
import MentorButtonRoutes from './ButtonRoutes';
import MentorProfileRoutes from './Routes';

type ContextType = {
  mentor: User | undefined;
};
export const MentorEditContext = React.createContext({} as ContextType);
const { Provider } = MentorEditContext;

const MentorEdit: FC = ({ children }) => {
  const { id } = useParams();

  const { data: mentor, error } = useSWR<User>(`/user/${id}`);

  const value = useMemo(
    () => ({
      mentor,
    }),
    [mentor],
  );

  return error ? (
    <p>L'utilisateur n'existe pas.</p>
  ) : mentor ? (
    <Provider value={value}>{children}</Provider>
  ) : (
        <LoaderContainer>
          <DefaultLoader size={1.5} />
        </LoaderContainer>
      );
};

const PageBackContainer = styled.div`
  margin-bottom: auto;
  font-size: 1.5rem;
`;
const TitleContainer = styled.div``;

const NameEmail = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 0.5rem;
`;

const Email = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: var(--gray-500);
  div:first-child {
    margin-right: 0.8rem;
  }
`;
const Head: FC = () => {
  const { mentor } = useContext(MentorEditContext);
  return (
    <PageHeadContainer>
      <PageTitle>
        <PageBackContainer>
          <PageBack href="/admin/mentoring/mentors" />
        </PageBackContainer>
        <TitleContainer>
          <NameEmail>
            <h1>{mentor?.firstName + ' ' + mentor?.lastName}</h1>
            <Email>
              <div>&bull;</div>
              {mentor?.email}
            </Email>
          </NameEmail>
          <ProfileChipContainer>
            {mentor?.UserRole?.map(
              (role: { role: RoleValues }, idx: number) => (
                <RoleChip role={role.role} key={idx} />
              ),
            )}
            {mentor?.mentorStatus && (
              <MentorStatusChip status={mentor.mentorStatus} />
            )}
          </ProfileChipContainer>
        </TitleContainer>
      </PageTitle>
      <HeadButtons>
        <MentorButtonRoutes mentorId={mentor?.id} />
        {/* 
				DISABLED
				<Button color="primary" icon={<ChevronDownIcon />} iconSide='right'>
					Actions
				</Button> */}
      </HeadButtons>
    </PageHeadContainer>
  );
};

const Navigation: FC = () => {
  const { mentor } = useContext(MentorEditContext);
  const baseURL = useMemo(() => {
    return `/admin/mentoring/mentors/${mentor?.id}/`;
  }, [mentor]);
  return (
    <ProfileNavigation>
      <ProfileLink to={baseURL}>Informations générales</ProfileLink>
      <ProfileLink to={baseURL + 'form'}>Formulaire mentor</ProfileLink>
      {(mentor?.mentorStatus === 'ACCEPTED' || mentor?.mentorStatus === 'AVAILABLE' ||
        mentor?.mentorStatus === 'MENTORSHIP_ACTIVE') && (
          <ProfileLink
            to={baseURL + 'mentoring/ongoing'}
            activeLink={baseURL + 'mentoring'}
          >
            Mentorat
          </ProfileLink>
        )}
      {(
        ['TRAINING', 'TRAINING_REGISTERED'] as (
          | MentorStatusValues
          | undefined
        )[]
      ).includes(mentor?.mentorStatus) && (
          <ProfileLink to={baseURL + 'training'}>Formation</ProfileLink>
        )}
      {mentor?.mentorStatus === 'MENTORSHIP_ACTIVE' && (
        <ProfileLink to={baseURL + 'interviews/'}>
          Entretiens programmés
        </ProfileLink>
      )}
      {/* 
			DISABLED
			<ProfileLink to={baseURL + 'tools'}>Outils</ProfileLink>
			<ProfileLink to={baseURL + 'transaction-history'}>Historiques transactions</ProfileLink> */}
    </ProfileNavigation>
  );
};

const Usage: FC = (props) => {
  return (
    <MentorEdit {...props}>
      <Head />
      <Navigation />
      <MentorProfileRoutes />
    </MentorEdit>
  );
};

export default Usage;
